import {
  AccountConnector,
  FormIntegrationEnum,
  HubspotAccountConnector,
  useConfigurationPageQuery,
  useFieldMappingsQuery,
  useFormConfigurationPageQuery,
} from '~/generated/graphql';
import { useCurrentAccount } from '~/hooks/account';
import { configurations, dataSources } from '~/lib/configuration';

import { useHomeCompanies } from './useHomeCompanies';

const getConnectorsByService = (connectors: AccountConnector[]) => {
  const connectorsByService: Record<string, AccountConnector> = {};

  if (connectors) {
    connectors.forEach((connector) => {
      if (connectorsByService[connector.service]) {
        if (connector.status === 'active')
          connectorsByService[connector.service] = connector;
      } else {
        connectorsByService[connector.service] = connector;
      }
    });
  }

  return connectorsByService;
};

const getSalesforceConnector = (
  connectors: Record<string, AccountConnector>,
) => {
  const connector = connectors[configurations.salesforce];

  if (connector?.status === 'active') {
    return connector;
  }

  return connectors[configurations.salesforcesandbox];
};

export const useHomeIntegrations = () => {
  const { account, loading } = useCurrentAccount();

  // CRM Configuration
  const connectors = account?.connectors;
  const connectorsByService = getConnectorsByService(
    connectors as AccountConnector[],
  );
  const salesforceConnector = getSalesforceConnector(connectorsByService);

  const hubspotConnector = connectorsByService[
    configurations.hubspot
  ] as HubspotAccountConnector;

  const defaultConnector = salesforceConnector || hubspotConnector;
  const isHubSpotConnected = !!hubspotConnector;

  // Clearbit JavaScript Configuration
  const { data: configurationData } = useConfigurationPageQuery();
  const isTagConnected = configurationData?.account?.accountSetting?.dataSources?.includes(
    dataSources.clearbitjs,
  );

  // Forms Configuration
  const { data: formConfigurationData } = useFormConfigurationPageQuery({
    variables: {
      integration: FormIntegrationEnum.Hubspot,
    },
  });
  const formConfiguration = formConfigurationData?.formConfiguration;
  const hasFormsAllowedSites = !!(
    formConfiguration?.allowedSites &&
    formConfiguration?.allowedSites.length > 0
  );
  const hasFormsAutoFill = !!formConfiguration?.autofill;

  // Forms Field Mappings
  const { data: formFieldMappingData } = useFieldMappingsQuery({
    variables: {
      integration: FormIntegrationEnum.Hubspot,
    },
  });
  const fieldMappings = formFieldMappingData?.formFieldMapping?.fieldMappings;
  const hasFormsFieldMappings = !!fieldMappings && fieldMappings.length > 0;

  // Target Market Configuration
  const { hasTargetMarketDefinedIcp } = useHomeCompanies();

  const hasTargetMarketAddedCompanies = false;

  // Visitors Configuration
  const hasVisitorsIntentCriteria = false;
  const hasVisitorsAddedCompanies = false;

  return {
    defaultConnector,
    hubspotConnector,
    hasFormsAllowedSites,
    hasFormsAutoFill,
    hasFormsFieldMappings,
    hasTargetMarketAddedCompanies,
    hasTargetMarketDefinedIcp,
    hasVisitorsAddedCompanies,
    hasVisitorsIntentCriteria,
    isHubSpotConnected,
    isTagConnected,
    loading,
    salesforceConnector,
  };
};
